import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Container, Typography } from '@mui/material';
import { joinSession, studentStreamUrl, submitResponse } from './api';
import { noteQuestionShown } from './storage';
import { useLiveState } from './useLiveState';
import { useStudentScore } from './useStudentScore';
import QuestionCounter from './QuestionCounter';
import QuestionOptions from './QuestionOptions';
import QuestionPrompt from './QuestionPrompt';
import ScoreReveal from './ScoreReveal';
import StudentScoreBar from './StudentScoreBar';

function StudentJoin() {
    const { joinCode } = useParams();
    const [phase, setPhase] = useState('joining');
    const [joined, setJoined] = useState(null);
    const [displayName, setDisplayName] = useState(null);
    const [streamUrl, setStreamUrl] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);

    // The stream only opens once the join is accepted, so a bad code never
    // leaves a stream retrying against a session that does not exist.
    const { state: streamed, failed: streamFailed } = useLiveState(streamUrl);
    const state = streamed ?? joined;

    useEffect(() => {
        let cancelled = false;

        joinSession(joinCode)
            .then(({ state: joinedState, displayName: name }) => {
                if (cancelled) return;
                setJoined(joinedState);
                setDisplayName(name);
                setPhase('joined');
                setStreamUrl(studentStreamUrl(joinCode));
            })
            .catch((error) => {
                if (cancelled) return;
                const status = error.response?.status;
                if (status === 404) setPhase('unknown');
                else if (status === 410) setPhase('ended');
                else setPhase('error');
            });

        return () => {
            cancelled = true;
            setStreamUrl(null);
        };
    }, [joinCode]);

    // Nothing more will ever arrive on this stream once the quiz has ended or
    // the connection has failed for good, so stop holding it open: nulling the
    // url tears down the EventSource via useLiveState's own cleanup, which in
    // turn closes the underlying request and lets the server drop its side too.
    useEffect(() => {
        if (streamed?.status === 'ended') {
            setStreamUrl(null);
        } else if (streamFailed) {
            setStreamUrl(null);
            setPhase('error');
        }
    }, [streamed?.status, streamFailed]);

    // Only ever drives the highlight — the server is the record of what was
    // answered — so a new question clears it and stale selections can't survive.
    // Not `status`: the timing clock below must survive a reveal, which changes
    // status but not ord.
    const ord = state?.question?.ord;
    useEffect(() => {
        setSelectedOption(null);
        if (ord !== undefined) noteQuestionShown(joinCode, ord);
    }, [joinCode, ord]);

    // Optimistic, but rolled back on failure: the highlight must never claim an
    // answer the server refused. Tap targets stay live, so a retry is one tap.
    const chooseOption = async (index) => {
        const previous = selectedOption;
        setSelectedOption(index);
        try {
            await submitResponse(joinCode, index, state.question.ord);
        } catch (error) {
            console.error('Could not record answer', error);
            setSelectedOption(previous);
        }
    };

    const { earnedPoints, totalScore } = useStudentScore(joinCode, state, selectedOption);

    // The join-time 410 and a live SSE transition to status:'ended' both mean
    // the same thing to a student who is already on this page — fold them into
    // one phase rather than teaching the message table two ways to say it.
    const effectivePhase = phase === 'joined' && state?.status === 'ended' ? 'ended' : phase;

    const message = {
        joining: { title: 'Joining…', body: null },
        joined: { title: displayName ? `You are ${displayName}` : 'You are in', body: 'Waiting for the host to start the quiz.' },
        unknown: { title: 'Quiz not found', body: `No live quiz is using the code ${joinCode}. Check the code on screen.` },
        ended: { title: 'This quiz has ended', body: 'Nothing more to do here.' },
        error: { title: 'Something went wrong', body: 'Could not reach the quiz. Try again in a moment.' },
    }[effectivePhase];

    const showingQuestion = effectivePhase === 'joined' && (state?.status === 'showing' || state?.status === 'revealed') && state.question;

    return (
        <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <Box sx={{ width: '100%' }}>
                {effectivePhase === 'joining' && <CircularProgress sx={{ mb: 3 }} />}

                {effectivePhase === 'joined' && displayName && (
                    <StudentScoreBar displayName={displayName} totalScore={totalScore} />
                )}

                {state?.quizTitle && effectivePhase === 'joined' && (
                    <Typography color="text.secondary" sx={{ mb: 1 }}>{state.quizTitle}</Typography>
                )}

                {showingQuestion ? (
                    <>
                        <QuestionCounter ord={state.question.ord} count={state.questionCount} fontSize="0.95rem" />
                        <QuestionPrompt prompt={state.question.prompt} variant="h5" sx={{ mb: 3 }} />

                        <QuestionOptions
                            question={state.question}
                            revealed={state.status === 'revealed'}
                            selected={selectedOption}
                            onSelect={state.status === 'showing' ? chooseOption : undefined}
                        />

                        {state.status === 'revealed' && (
                            <ScoreReveal
                                earnedPoints={earnedPoints}
                                answered={selectedOption !== null}
                                ord={state.question.ord}
                            />
                        )}
                    </>
                ) : (
                    <>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 800,
                                mb: 2,
                                color: effectivePhase === 'joined' ? 'primary.main' : 'inherit',
                            }}
                        >
                            {message.title}
                        </Typography>
                        {message.body && (
                            <Typography color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                                {message.body}
                            </Typography>
                        )}

                        {effectivePhase === 'joined' && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 4, fontSize: '1rem' }}>
                                Keep this page open.
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        </Container>
    );
}

export default StudentJoin;
